import { useCallback } from "react";

import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import { Button, Select, Spin, TreeSelect } from "antd";
import { IS_INTERNAL_ENV } from "constants/app.constants";
import { UnitSystem } from "types/units";

import { useEvaUnitCore } from "hooks/useEvaUnitCore";
import useUserUnit from "hooks/useUserUnit";

import { IconSpinner } from "components/icons";
import SegmentedToggle from "components/shared/SegmentedToggle";

import { useIpdbStore } from "../../contexts/IpdbStoreProvider";
import {
  selectAllFieldsDataForSelectedZones,
  selectDataErrorMsg,
  selectExpandedKeys,
  selectModelSource,
  selectOrgLayerKeyToGroup,
  selectOrgLayerKeyToName,
  selectSearchValue,
  selectSelectedIpdbFieldData,
  selectSelectedZoneKeys,
  selectTreeData,
  selectUseNativeSource
} from "../../contexts/ipdbSelectors";
import { type IpdbState } from "../../contexts/ipdbStore";
import type { SetSelectedIpdbFieldData } from "../Ipdb";
import { THREE_D_MODEL, TWO_D_MODEL } from "../Ipdb";
import { extractZoneNames } from "../utils/extractZoneNames";
import {
  IpdbOptionItem,
  SectionLabelContainer,
  StyledAlert,
  SwitcherIcon,
  ZoneInputContainer
} from "./shared/IpdbStyledComponents";

const { Option } = Select;

interface IpdbDataSelectionProps {
  showAddGeoMapButton: boolean;
  isGeoMapUploading: boolean;
  uploadProgress: number;
  onOpenGeoMapModal: () => void;
  getInitialModelSource: () => string;
  setSelectedIpdbFieldData: SetSelectedIpdbFieldData;
}

export default function IpdbDataSelection({
  showAddGeoMapButton,
  isGeoMapUploading,
  uploadProgress,
  onOpenGeoMapModal,
  getInitialModelSource,
  setSelectedIpdbFieldData
}: IpdbDataSelectionProps) {
  const modelSource = useIpdbStore(selectModelSource);
  const selectedIpdbFieldData = useIpdbStore(selectSelectedIpdbFieldData);
  const selectedZoneKeys = useIpdbStore(selectSelectedZoneKeys);
  const useNativeSource = useIpdbStore(selectUseNativeSource);
  const allFieldsDataForSelectedZones = useIpdbStore(selectAllFieldsDataForSelectedZones);
  const searchValue = useIpdbStore(selectSearchValue);
  const treeData = useIpdbStore(selectTreeData);
  const orgLayerKeyToName = useIpdbStore(selectOrgLayerKeyToName);
  const orgLayerKeyToGroup = useIpdbStore(selectOrgLayerKeyToGroup);
  const expandedKeys = useIpdbStore(selectExpandedKeys);
  const dataErrorMsg = useIpdbStore(selectDataErrorMsg);
  const updateIpdbState = useIpdbStore((s) => s.update);

  const { isReady: isUnitCoreReady, getTargetUnit } = useEvaUnitCore();
  const unitType = useUserUnit();

  const setField = useCallback(
    (payload: Partial<IpdbState>) => updateIpdbState(payload),
    [updateIpdbState]
  );

  // Keep group headers always expanded
  const GROUP_HEADER_KEYS = ["eva_research_group", "organization_group"];
  const handleTreeExpand = (keys: string[]) => {
    const newKeys = [...new Set([...keys, ...GROUP_HEADER_KEYS])];
    setField({ expandedKeys: newKeys });
  };

  return (
    <>
      <SectionLabelContainer>Data Selection</SectionLabelContainer>

      {/* Unified Source toggle: Net/Gross for Eva Research (internal), Original/LSD for org layers */}
      {IS_INTERNAL_ENV &&
        (modelSource === TWO_D_MODEL || modelSource === THREE_D_MODEL) && (
          <IpdbOptionItem className="ipdb-option-item">
            <label>Source</label>
            <SegmentedToggle
              data-testid="geo-model-segmented-select"
              value={modelSource}
              options={[
                { label: "Net", value: TWO_D_MODEL },
                { label: "Gross", value: THREE_D_MODEL }
              ]}
              onChange={(value) => {
                setField({
                  modelSource: value as string,
                  selectedZoneKeys: [],
                  selectedIpdbZones: [],
                  selectedIpdbFieldData: {
                    ...selectedIpdbFieldData,
                    name: ""
                  }
                });
              }}
            />
          </IpdbOptionItem>
        )}
      {modelSource !== TWO_D_MODEL && modelSource !== THREE_D_MODEL && (
        <IpdbOptionItem className="ipdb-option-item">
          <label>Source</label>
          <SegmentedToggle
            data-testid="geo-source-segmented-select"
            value={useNativeSource ? "original" : "lsd"}
            options={[
              { label: "LSD", value: "lsd" },
              { label: "Original", value: "original" }
            ]}
            onChange={(value) => setField({ useNativeSource: value === "original" })}
          />
        </IpdbOptionItem>
      )}

      <IpdbOptionItem
        className="ipdb-option-item"
        isGeoButtonVisible={showAddGeoMapButton}>
        <label data-testid="zone-title" className="ipdb-option-zone-label">
          Zone
        </label>
        <ZoneInputContainer>
          {showAddGeoMapButton && (
            <Button type="link" onClick={onOpenGeoMapModal} disabled={isGeoMapUploading}>
              {isGeoMapUploading ? `Uploading... (${uploadProgress}%)` : "+ Add Geo Map"}
              {isGeoMapUploading && <Spin indicator={<IconSpinner />} />}
            </Button>
          )}
          <TreeSelect
            showSearch
            data-testid="geo-zone-select"
            suffixIcon={<KeyboardArrowDownIcon fontSize="large" />}
            switcherIcon={
              <SwitcherIcon role="img">
                <KeyboardArrowDownIcon fontSize="large" />
              </SwitcherIcon>
            }
            value={selectedZoneKeys}
            dropdownStyle={{ overflow: "auto" }}
            placeholder="Please select"
            allowClear
            multiple
            maxTagCount={10}
            onChange={(zones: string[]) => {
              const actualZones = extractZoneNames(zones, orgLayerKeyToName);

              if (actualZones.length === 0) {
                setField({
                  selectedZoneKeys: zones,
                  selectedIpdbZones: actualZones,
                  modelSource: getInitialModelSource(),
                  useNativeSource: false,
                  selectedIpdbFieldData: { ...selectedIpdbFieldData, name: "" }
                });
                return;
              }

              const updates: Partial<IpdbState> = {
                selectedZoneKeys: zones,
                selectedIpdbZones: actualZones
              };

              const hasOrgLayerKey = zones.some(
                (key) => key.startsWith("org_layer_") || key.startsWith("org_cutoff_")
              );

              if (hasOrgLayerKey) {
                const orgKey = zones.find(
                  (key) => key.startsWith("org_layer_") || key.startsWith("org_cutoff_")
                );
                const group = orgLayerKeyToGroup.get(orgKey);

                if (group) {
                  updates.modelSource = group;
                } else {
                  const defaultSource = getInitialModelSource();
                  if (defaultSource) {
                    updates.modelSource = defaultSource;
                  }
                }
              }

              setField(updates);
            }}
            onSearch={(value) => setField({ searchValue: value })}
            searchValue={searchValue}
            treeCheckable={true}
            treeData={treeData}
            treeExpandedKeys={expandedKeys}
            onTreeExpand={handleTreeExpand}
            popupClassName="geo-zone-tree-dropdown"
          />
        </ZoneInputContainer>
      </IpdbOptionItem>

      <IpdbOptionItem className="ipdb-option-item">
        <label>Field</label>
        <Select
          value={selectedIpdbFieldData.name}
          data-testid="geo-field-select"
          popupClassName="modal-select ipdb-field-select"
          onChange={(fieldValue: string) => {
            setField({ isBinUsingDefaultValues: true });
            setSelectedIpdbFieldData((prev) => ({
              ...prev,
              name: fieldValue
            }));
          }}>
          {allFieldsDataForSelectedZones?.length
            ? allFieldsDataForSelectedZones.map((field) => {
                const sourceUnit = field.unit?.replace(/[()]/g, "");
                const displayUnit =
                  sourceUnit && isUnitCoreReady && unitType !== UnitSystem.Hybrid
                    ? getTargetUnit(sourceUnit, unitType)
                    : sourceUnit;
                const fieldLabel = displayUnit
                  ? `${field.displayName} (${displayUnit})`
                  : field.displayName;
                return (
                  <Option key={field.name} value={field.name}>
                    {fieldLabel}
                  </Option>
                );
              })
            : null}
        </Select>
      </IpdbOptionItem>

      {dataErrorMsg && <StyledAlert message={dataErrorMsg} type="error" showIcon />}
    </>
  );
}
